/**
 * Pure helpers for the Temperature logs page's edit state (the shape of
 * `inventory-edit.ts`): the draft the page edits, the patches a save sends,
 * and the read-state value of a cell.
 *
 * A cell is editable only while it is OFFERED (a hot or chilled item; weekly
 * milk takes no received reading) and NOT SUBMITTED through Production
 * Records. Patches carry only the cells that changed against the stored
 * reading; a value cleared in the draft is sent as null so the server clears
 * it too. A value outside the log's range makes the whole draft unsaveable
 * (the server would refuse it), so `temperaturePatchesFromDraft` returns
 * undefined and the page keeps Save disabled.
 */

import type { TemperatureCellWire, TemperaturePatch, TemperatureRowWire } from '@/api/cafeteria-temperatures'

/** The log's range, mirrored from the receive-delivery validation (°F). */
export const TEMPERATURE_F_MIN = -20
export const TEMPERATURE_F_MAX = 250

export type TemperatureColumnKey = 'received' | 'served'
export const TEMPERATURE_COLUMN_KEYS: readonly TemperatureColumnKey[] = ['received', 'served']

/** The page's two columns (also what the skeleton renders). */
export const TEMPERATURE_PAGE_COLUMNS: ReadonlyArray<{
  key: TemperatureColumnKey
  header: string
  inputLabel: string
}> = [
  { key: 'received', header: 'Received (°F)', inputLabel: 'received temperature' },
  { key: 'served', header: 'Served (°F)', inputLabel: 'served temperature' },
]

export interface TemperatureDraftRow {
  received: number | null
  served: number | null
}

/** `itemKey` → the two values being edited (null = no reading). */
export type TemperatureDraft = Record<string, TemperatureDraftRow>

export function temperatureCellEditable(cell: TemperatureCellWire): boolean {
  return cell.offered && cell.submitted === null
}

export function temperatureInRange(value: number): boolean {
  return Number.isFinite(value) && value >= TEMPERATURE_F_MIN && value <= TEMPERATURE_F_MAX
}

/** A typed value the log cannot store; null (no reading) is never invalid. */
export function temperatureCellInvalid(value: number | null): boolean {
  return value !== null && !temperatureInRange(value)
}

/**
 * Whether a reading misses its item's HACCP holding band: a hot item below
 * 135°F, a chilled item above 41°F. Shelf-stable and unclassified items have no
 * band to miss, and no reading misses nothing. Display only: the record still
 * saves the reading, the way the paper prints an out-of-range one with its mark.
 */
export function outsideHoldingBand(
  value: number | null,
  temperatureClass: 'HOT' | 'CHILLED' | 'AMBIENT' | null,
): boolean {
  if (value === null || !Number.isFinite(value)) {
    return false
  }
  if (temperatureClass === 'HOT') {
    return value < 135
  }
  if (temperatureClass === 'CHILLED') {
    return value > 41
  }
  return false
}

/** "38.0°F"; an unknown reading is a dash, never a fake number. */
export function formatTemperatureF(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(1)}°F`
}

/** What a cell shows in the read state: the submitted fact once locked, else the cafeteria's reading. */
export function temperatureCellValue(cell: TemperatureCellWire): number | null {
  return cell.submitted ? cell.submitted.value : (cell.reading?.value ?? null)
}

export function draftFromTemperatureRows(rows: ReadonlyArray<TemperatureRowWire>): TemperatureDraft {
  const draft: TemperatureDraft = {}
  for (const row of rows) {
    draft[row.itemKey] = {
      received: row.received.reading?.value ?? null,
      served: row.served.reading?.value ?? null,
    }
  }
  return draft
}

/**
 * The rows whose editable cells changed, with only the changed columns set.
 * Undefined when a typed value is outside the log's range.
 */
export function temperaturePatchesFromDraft(
  rows: ReadonlyArray<TemperatureRowWire>,
  draft: TemperatureDraft,
): TemperaturePatch[] | undefined {
  const patches: TemperaturePatch[] = []
  let invalid = false
  for (const row of rows) {
    const draftRow = draft[row.itemKey]
    if (!draftRow) {
      continue
    }
    const patch: TemperaturePatch = { itemKey: row.itemKey }
    let changed = false
    for (const key of TEMPERATURE_COLUMN_KEYS) {
      const cell = row[key]
      if (!temperatureCellEditable(cell)) {
        continue
      }
      const next = draftRow[key]
      if (temperatureCellInvalid(next)) {
        invalid = true
      }
      if (next !== (cell.reading?.value ?? null)) {
        if (key === 'received') {
          patch.receivedTemperatureF = next
        } else {
          patch.servedTemperatureF = next
        }
        changed = true
      }
    }
    if (changed) {
      patches.push(patch)
    }
  }
  return invalid ? undefined : patches
}

export function editableTemperatureCellCount(rows: ReadonlyArray<TemperatureRowWire>): number {
  let count = 0
  for (const row of rows) {
    for (const key of TEMPERATURE_COLUMN_KEYS) {
      if (temperatureCellEditable(row[key])) {
        count += 1
      }
    }
  }
  return count
}

/** Every offered cell has been submitted through Production Records (and there is at least one). */
export function temperaturesFullyLocked(rows: ReadonlyArray<TemperatureRowWire>): boolean {
  let offered = 0
  for (const row of rows) {
    for (const key of TEMPERATURE_COLUMN_KEYS) {
      const cell = row[key]
      if (!cell.offered) {
        continue
      }
      offered += 1
      if (cell.submitted === null) {
        return false
      }
    }
  }
  return offered > 0
}

/** The shape `TemperatureTable` renders for one cell (structural; the table's own type matches). */
export interface TemperatureTableCellData {
  offered: boolean
  value: number | null
  reading: TemperatureCellWire['reading']
  submitted: TemperatureCellWire['submitted']
}

/**
 * One table cell for the page: while editing, an editable cell shows the
 * draft's value; everything else shows the read-state value (the submitted
 * fact once locked, else the cafeteria's reading).
 */
export function temperatureTableCell(
  cell: TemperatureCellWire,
  draftValue: number | null | undefined,
): TemperatureTableCellData {
  return {
    offered: cell.offered,
    value: draftValue !== undefined && temperatureCellEditable(cell) ? draftValue : temperatureCellValue(cell),
    reading: cell.reading,
    submitted: cell.submitted,
  }
}
